/* SCHAHAM · quiet motion: hairlines draw, fade-up once, number count-up, hero drift, mobile sticky buy bar.
   Everything is skipped under prefers-reduced-motion (content shows immediately). */
(() => {
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  root.classList.add('ff-js');

  const WATCH = '[data-ff-reveal], .ff-hero__cap, .ff-index__list, .ff-spec, .ff-biz__tracks, .ff-steps, .ff-bento__head';

  const countUp = (el) => {
    if (reduce || el.dataset.ffCounted) return;
    el.dataset.ffCounted = '1';
    const final = el.textContent;
    const nums = final.match(/\d+/g);
    if (!nums) return;
    const t0 = performance.now(), dur = 900;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - p, 3);
      let i = 0;
      el.textContent = final.replace(/\d+/g, () => String(Math.round(Number(nums[i++]) * e)));
      if (p < 1) requestAnimationFrame(step); else el.textContent = final;
    };
    requestAnimationFrame(step);
  };

  const show = (el) => {
    el.classList.add('is-in');
    el.querySelectorAll('[data-ff-count]').forEach(countUp);
    if (el.matches('[data-ff-count]')) countUp(el);
  };

  const io = reduce ? null : new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { show(e.target); io.unobserve(e.target); } });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });

  const scan = (scope) => {
    scope.querySelectorAll(WATCH).forEach((el) => {
      if (el.dataset.ffSeen) return;
      el.dataset.ffSeen = '1';
      // stagger children lines
      el.querySelectorAll(':scope > *').forEach((c, i) => c.style.setProperty('--i', Math.min(i, 12)));
      reduce ? el.classList.add('is-in') : io.observe(el);
    });
  };

  // Hero drift: the photo moves up to ~24px slower than the page while the hero is on screen
  const drift = () => {
    if (reduce) return;
    const imgs = document.querySelectorAll('.ff-hero__media img');
    if (!imgs.length) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      imgs.forEach((img) => {
        const r = img.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > innerHeight) return;
        const p = Math.max(0, Math.min(1, -r.top / r.height));
        img.style.setProperty('--ff-drift', (p * 24).toFixed(1) + 'px');
      });
    };
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  };

  // Mobile sticky buy bar: shows once the real buy button scrolls out of view; tapping it presses the real button
  const sticky = () => {
    if (sticky.clickReady || !document.querySelector('.ff-sticky')) return;
    sticky.clickReady = true;
    const barNow = () => document.querySelector('.ff-sticky');
    const sectionNow = () => barNow()?.closest('.shopify-section--main-product')
      || document.querySelector('.shopify-section--main-product');
    const resolve = () => sectionNow()?.querySelector('form[action*="/cart/add"] button[type="submit"]')
      || document.querySelector('product-form button[type="submit"], form[action*="/cart/add"] button[type="submit"]');
    const formNow = () => {
      const section = sectionNow();
      const formId = section?.querySelector('variant-picker[form-id]')?.getAttribute('form-id');
      return (formId && document.getElementById(formId)) || resolve()?.form
        || section?.querySelector('form[action*="/cart/add"]');
    };
    let btn = resolve(), section = sectionNow(), buttonAbove = false, on = false, open = false, previousBar = null;
    const apply = () => {
      const bar = barNow();
      root.classList.toggle('ff-sticky-on', !!bar && on);
      if (!bar) return;
      if (!bar.querySelector('[data-ff-panel]')) open = false;
      const replaced = previousBar !== bar;
      if (replaced && open) bar.classList.add('no-anim');
      bar.classList.toggle('is-on', on);
      bar.classList.toggle('is-open', open);
      if (open) bar.removeAttribute('aria-hidden');
      else bar.setAttribute('aria-hidden', 'true');
      bar.querySelector('[data-ff-choose]')?.setAttribute('aria-expanded', String(open));
      bar.querySelectorAll('[data-ff-buy], [data-ff-opt], [data-ff-close], [data-ff-choose]').forEach((el) => el.setAttribute('tabindex', open ? '0' : '-1'));
      previousBar = bar;
      if (replaced && open) requestAnimationFrame(() => requestAnimationFrame(() => bar.classList.remove('no-anim')));
    };
    const close = () => {
      const bar = barNow();
      const returnFocus = open && bar?.contains(document.activeElement);
      open = false;
      apply();
      if (returnFocus) bar.querySelector('[data-ff-buy]')?.focus({ preventScroll: true });
    };
    const recompute = () => {
      section = sectionNow();
      // read the button directly too, so jumps (back to top, Home key) that skip the observer still hide the bar
      if (btn?.isConnected) buttonAbove = btn.getBoundingClientRect().bottom <= 0;
      // Allon 2026-11-04: the buy bar stays visible on product pages at all times (phone and desktop).
      on = !!section?.isConnected || !!barNow();
      if (!on) close();
      else apply();
    };
    const observer = new IntersectionObserver(([e]) => {
      buttonAbove = !e.isIntersecting && e.boundingClientRect.top < 0;
      recompute();
    });
    if (btn) observer.observe(btn);
    let ticking = false;
    const schedule = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        section = sectionNow();
        recompute();
      });
    };
    addEventListener('scroll', schedule, { passive: true });
    addEventListener('resize', schedule);
    const refresh = () => {
      section = sectionNow();
      const next = resolve();
      if (next !== btn) {
        if (btn) observer.unobserve(btn);
        btn = next;
        if (!btn) buttonAbove = false;
        if (btn?.isConnected) observer.observe(btn);
      }
      recompute();
    };
    new MutationObserver(refresh).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('variant:change', refresh);
    document.addEventListener('click', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const bar = barNow();
      if (!bar || !target) return;
      if (!bar.contains(target)) { close(); return; }
      if (target.closest('[data-ff-choose]')) {
        open = !open;
        apply();
        if (open) {
          const firstGroup = bar.querySelector('.ff-sticky__group');
          (firstGroup?.querySelector('[data-ff-opt][aria-pressed="true"]:not(:disabled)')
            || firstGroup?.querySelector('[data-ff-opt]:not(:disabled)'))?.focus({ preventScroll: true });
        }
        return;
      }
      const buy = target.closest('[data-ff-buy]');
      if (buy) {
        if (bar.dataset.ffVariants === 'multi' && !open) {
          open = true;
          apply();
          const firstGroup = bar.querySelector('.ff-sticky__group');
          (firstGroup?.querySelector('[data-ff-opt][aria-pressed="true"]:not(:disabled)')
            || firstGroup?.querySelector('[data-ff-opt]:not(:disabled)'))?.focus({ preventScroll: true });
        } else {
          const next = resolve();
          if (next?.isConnected) next.click();
          close();
        }
        return;
      }
      if (target.closest('[data-ff-close]')) { close(); return; }
      const pill = target.closest('[data-ff-opt]');
      if (!pill || pill.disabled) return;
      const form = formNow();
      const input = form && Array.from(sectionNow().querySelectorAll('input[data-option-position]')).find((el) =>
        el.getAttribute('form') === form.getAttribute('id') && el.dataset.optionPosition === pill.dataset.position && el.value === pill.dataset.valueId);
      if (!input) return;
      bar.querySelectorAll('[data-ff-opt]').forEach((el) => {
        if (el.dataset.position === pill.dataset.position) el.setAttribute('aria-pressed', String(el === pill));
      });
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    document.addEventListener('pointerdown', (e) => {
      if (open && !barNow()?.contains(e.target)) close();
    });
    document.addEventListener('keydown', (e) => {
      if (open && e.key === 'Escape') { e.preventDefault(); close(); }
    });
    refresh();
  };


  // Hero slideshow: native swipe (scroll-snap), pill-to-dot indicators, slow autoplay that pauses on hover/touch/focus/hidden tab.
  const slideshow = (root) => {
    if (root.dataset.ffReady) return;
    root.dataset.ffReady = '1';
    const track = root.querySelector('.ff-hero-track');
    const slides = [...root.querySelectorAll('[data-ff-slide]')];
    const dots = [...root.querySelectorAll('[data-ff-dot]')];
    if (!track || slides.length < 2) return;
    let cur = -1;
    const auto = root.dataset.autoplay === 'true' && !reduce;
    root.style.setProperty('--ff-slide-ms', '7000ms');
    const pauses = new Set(['offscreen']);
    const setPause = (reason, paused) => {
      paused ? pauses.add(reason) : pauses.delete(reason);
      root.classList.toggle('is-paused', pauses.size > 0);
    };
    const positionNav = () => {
      const media = slides[0].querySelector('.ff-hero__media');
      if (!media) return;
      // Every slide shares this layout. Slide-local offsets ignore track scrolling in both directions.
      const left = Math.max(0, Math.min(root.clientWidth, track.offsetLeft + media.offsetLeft));
      const width = Math.max(0, Math.min(media.offsetWidth, root.clientWidth - left));
      root.style.setProperty('--ff-nav-top', `${track.offsetTop + media.offsetTop + media.offsetHeight}px`);
      root.style.setProperty('--ff-nav-left', `${left}px`);
      root.style.setProperty('--ff-nav-w', `${width}px`);
    };
    const restart = () => {
      dots.forEach((dot) => dot.classList.remove('is-running'));
      if (!auto || !dots[cur]) return;
      void dots[cur].offsetWidth;
      dots[cur].classList.add('is-running');
    };
    let unsnapTimer = null;
    const go = (i, smooth = true) => {
      i = (i + slides.length) % slides.length;
      mark(i);
      const dx = slides[i].getBoundingClientRect().left - track.getBoundingClientRect().left;
      if (!smooth || reduce) { track.scrollBy({ left: dx, behavior: 'auto' }); return; }
      // Snap would stop a multi-slide smooth scroll at the next slide: switch it off for the move.
      track.style.scrollSnapType = 'none';
      track.scrollBy({ left: dx, behavior: 'smooth' });
      clearTimeout(unsnapTimer);
      const done = () => { clearTimeout(unsnapTimer); track.style.scrollSnapType = ''; track.removeEventListener('scrollend', done); };
      track.addEventListener('scrollend', done);
      unsnapTimer = setTimeout(done, 900);
    };
    const mark = (i) => {
      if (i === cur) return;
      cur = i;
      dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
      slides.forEach((s, k) => {
        s.setAttribute('aria-hidden', k === i ? 'false' : 'true');
        s.querySelectorAll('a, button').forEach((el) => { k === i ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1'); });
        if (k === i) s.querySelectorAll('.ff-hero__cap').forEach((c) => c.classList.add('is-in'));
      });
      restart();
    };
    const io2 = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting && track.style.scrollSnapType !== 'none') mark(slides.indexOf(e.target)); }),
      { root: track, threshold: 0.6 });
    slides.forEach((s) => io2.observe(s));
    dots.forEach((d, k) => d.addEventListener('click', () => { if (k === cur) restart(); else go(k); }));
    // Keyboard: instant, no animation (keyboard actions repeat and must feel immediate)
    root.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.target.closest('input, textarea, select')) return;
      const rtl = getComputedStyle(root).direction === 'rtl';
      const fwd = (e.key === 'ArrowLeft') === rtl;
      const next = (cur + (fwd ? 1 : -1) + slides.length) % slides.length;
      e.preventDefault(); go(next, false);
    });
    dots.forEach((dot, i) => dot.addEventListener('animationend', (e) => {
      if (e.animationName === 'ff-hero-shrink'
        && auto && !pauses.size && i === cur) go(cur + 1);
    }));
    root.addEventListener('mouseenter', () => setPause('hover', true));
    root.addEventListener('mouseleave', () => setPause('hover', false));
    root.addEventListener('focusin', () => setPause('focus', true));
    root.addEventListener('focusout', (e) => {
      if (!root.contains(e.relatedTarget)) setPause('focus', false);
    });
    let touchTimer = null;
    root.addEventListener('touchstart', () => {
      clearTimeout(touchTimer);
      setPause('touch', true);
    }, { passive: true });
    const touchEnd = () => {
      clearTimeout(touchTimer);
      touchTimer = setTimeout(() => setPause('touch', false), 4000);
    };
    root.addEventListener('touchend', touchEnd, { passive: true });
    root.addEventListener('touchcancel', touchEnd, { passive: true });
    document.addEventListener('visibilitychange', () => setPause('hidden', document.hidden));
    setPause('hidden', document.hidden);
    // Only autoplay while at least half of the hero is on screen.
    new IntersectionObserver(([e]) => {
      setPause('offscreen', !e.isIntersecting || e.intersectionRatio < 0.5);
    }, { threshold: 0.5 }).observe(root);
    const mediaObserver = new ResizeObserver(positionNav);
    slides.forEach((slide) => {
      const media = slide.querySelector('.ff-hero__media');
      if (media) mediaObserver.observe(media);
    });
    mediaObserver.observe(root);
    mediaObserver.observe(track);
    window.addEventListener('resize', positionNav);
    mark(0);
  };

  // WhatsApp drawer (sections/ff-whatsapp.liquid). Native <dialog>: focus trap, Esc and backdrop close for free.
  // Panel enters 280ms ease-out, leaves 220ms; reduced motion = instant.
  const wa = () => {
    const dlg = document.querySelector('[data-ff-wa]');
    if (!dlg || dlg.dataset.ffReady) return;
    dlg.dataset.ffReady = '1';
    const input = dlg.querySelector('.ff-wa__input'), log = dlg.querySelector('[data-ff-wa-log]');
    const pageBox = dlg.querySelector('[data-ff-wa-page]');
    const still = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    const open = () => {
      if (dlg.open) return;
      dlg.showModal();
      requestAnimationFrame(() => requestAnimationFrame(() => dlg.classList.add('is-open')));
    };
    const close = () => {
      if (!dlg.open) return;
      dlg.classList.remove('is-open');
      still() ? dlg.close() : setTimeout(() => dlg.close(), 220);
    };
    const send = (text, phone, fallback) => {
      const page = document.title + ' ' + location.href;
      let t = (text || '').trim() || fallback || '';
      if (pageBox && pageBox.checked) t = t.includes('[[PAGE]]') ? t.split('[[PAGE]]').join(page) : t + '\n' + page;
      else t = t.split('[[PAGE]]').join('').trim();
      window.open('https://wa.me/' + phone + '?text=' + encodeURIComponent(t), '_blank', 'noopener');
    };
    dlg._ffOpen = open;
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
    dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target.closest('[data-ff-wa-close]')) close(); });
    dlg.querySelectorAll('[data-ff-wa-chip]').forEach((c) => c.addEventListener('click', () => { input.value = c.dataset.ffWaChip; input.focus(); }));
    dlg.querySelector('[data-ff-wa-form]').addEventListener('submit', (e) => {
      e.preventDefault();
      const v = input.value.trim();
      if (v) { const b = document.createElement('p'); b.className = 'ff-wa__bubble ff-wa__bubble--out'; b.textContent = v; log.appendChild(b); log.scrollTop = log.scrollHeight; }
      send(v, dlg.dataset.phone, dlg.dataset.message);
      input.value = '';
    });
    const en = dlg.querySelector('[data-ff-wa-en]');
    if (en) en.addEventListener('click', () => send('', dlg.dataset.enPhone, dlg.dataset.enMessage));
  };

  // one listener for the header button; looks the drawer up at click time (survives theme-editor reloads)
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-ff-wa-open]')) return;
    const d = document.querySelector('[data-ff-wa]');
    if (d && d._ffOpen) { e.preventDefault(); d._ffOpen(); }
  });

  const init = () => { scan(document); drift(); sticky(); wa(); document.querySelectorAll('[data-ff-slides]').forEach(slideshow); };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
  // Theme editor: re-scan sections when they reload
  document.addEventListener('shopify:section:load', (e) => { scan(e.target); wa(); e.target.querySelectorAll('[data-ff-slides]').forEach(slideshow); });
})();
